import { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import AdminMarketing from "./AdminMarketing";

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function toCSV(rows) {
  const headers = [
    "Date",
    "Name",
    "Phone",
    "Email",
    "Source (page)",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "Referrer",
    "First Landing",
    "Message",
  ];
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [
    headers.join(","),
    ...rows.map((r) =>
      [
        fmtDate(r.created_at),
        r.name,
        r.phone,
        r.email,
        r.source,
        r.utm_source,
        r.utm_medium,
        r.utm_campaign,
        r.utm_term,
        r.utm_content,
        r.referrer,
        r.first_landing,
        r.message,
      ]
        .map(esc)
        .join(",")
    ),
  ];
  return lines.join("\n");
}

export default function AdminLeads() {
  const hasToken =
    typeof window !== "undefined" &&
    !!localStorage.getItem("jfl_admin_token");
  const [activeTab, setActiveTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!hasToken) return;
    let live = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/consultations?limit=2000");
        if (live) setLeads(data);
      } catch (err) {
        if (live)
          setError(err?.response?.data?.detail || "Could not load leads.");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [hasToken]);

  const filtered = useMemo(() => {
    if (!q.trim()) return leads;
    const needle = q.toLowerCase();
    return leads.filter((l) =>
      [l.name, l.phone, l.email, l.message, l.source]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [leads, q]);

  if (!hasToken) {
    return <Navigate to="/admin/login" replace />;
  }

  const logout = () => {
    localStorage.removeItem("jfl_admin_token");
    nav("/admin/login", { replace: true });
  };

  const exportCSV = () => {
    const csv = toCSV(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `jos-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const purgeTestLeads = async () => {
    const matches = leads.filter((l) =>
      (l.name || "").toLowerCase().includes("test")
    );
    if (matches.length === 0) {
      alert("No leads with 'test' in the name were found.");
      return;
    }
    if (
      !window.confirm(
        `Remove ${matches.length} lead(s) where the name contains "test"? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      const { data } = await api.post("/admin/consultations/purge-test-leads");
      setLeads((cur) =>
        cur.filter((l) => !(l.name || "").toLowerCase().includes("test"))
      );
      if (selected && (selected.name || "").toLowerCase().includes("test")) {
        setSelected(null);
      }
      alert(`Removed ${data.deleted} test lead(s).`);
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not purge test leads.");
    }
  };

  const deleteLead = async (lead) => {
    if (!lead?.id) return;
    if (
      !window.confirm(
        `Delete this lead?\n\n${lead.name || "(no name)"} · ${lead.email || ""}`
      )
    ) {
      return;
    }
    try {
      await api.delete(`/admin/consultations/${lead.id}`);
      setLeads((cur) => cur.filter((l) => l.id !== lead.id));
      if (selected?.id === lead.id) setSelected(null);
    } catch (err) {
      alert(err?.response?.data?.detail || "Could not delete this lead.");
    }
  };

  return (
    <main
      data-testid="admin-leads-page"
      className="min-h-screen bg-paper text-ink"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-paper/95 backdrop-blur-sm border-b border-gray-200/60">
        <div className="flex items-center justify-between px-5 md:px-10 lg:px-14 py-4">
          <div className="flex items-center gap-4">
            <img
              src="/brand/jos-logo.png"
              alt="Jos Family Law"
              className="h-8 md:h-9 w-auto"
            />
            <span className="hidden md:inline-block font-mono text-[10px] tracking-[0.28em] uppercase text-gray-600 border-l border-gray-400/40 pl-4">
              Admin Dashboard
            </span>
          </div>
          <div className="flex items-center gap-3">
            {activeTab === "leads" && (
              <>
                <button
                  type="button"
                  onClick={purgeTestLeads}
                  data-testid="admin-purge-test-leads"
                  className="font-mono text-[10px] md:text-[11px] tracking-[0.24em] uppercase border border-gray-400 text-gray-700 hover:bg-red-600 hover:text-paper hover:border-red-600 transition-colors px-4 py-2"
                >
                  Remove Test Leads
                </button>
                <button
                  type="button"
                  onClick={exportCSV}
                  data-testid="admin-export-csv"
                  className="font-mono text-[10px] md:text-[11px] tracking-[0.24em] uppercase border border-ink text-ink hover:bg-ink hover:text-paper transition-colors px-4 py-2"
                >
                  Export CSV
                </button>
              </>
            )}
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout"
              className="font-mono text-[10px] md:text-[11px] tracking-[0.24em] uppercase text-gray-600 hover:text-ink transition-colors px-3 py-2"
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Tab nav */}
        <nav className="px-5 md:px-10 lg:px-14 border-t border-gray-200/60">
          <div className="flex gap-6" data-testid="admin-tabs">
            {[
              { id: "leads", label: "Leads" },
              { id: "marketing", label: "Marketing" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={
                  "py-3 font-mono text-[11px] tracking-[0.24em] uppercase border-b-2 transition-colors " +
                  (activeTab === t.id
                    ? "border-teal text-ink"
                    : "border-transparent text-gray-500 hover:text-ink")
                }
              >
                {t.label}
              </button>
            ))}
          </div>
        </nav>
      </header>

      {activeTab === "marketing" ? (
        <div className="px-5 md:px-10 lg:px-14 py-10 md:py-14">
          <AdminMarketing />
        </div>
      ) : (
      <div className="px-5 md:px-10 lg:px-14 py-10 md:py-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-gray-600 mb-3">
              Consultation Requests
            </p>
            <h1 className="font-display text-[44px] md:text-[60px] leading-[1.04] tracking-tight">
              <span className="italic">{filtered.length}</span>{" "}
              {filtered.length === 1 ? "lead" : "leads"}
            </h1>
          </div>
          <div className="w-full md:w-[320px]">
            <label
              htmlFor="search"
              className="block font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600 mb-2"
            >
              Search
            </label>
            <input
              id="search"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, email, phone, message…"
              data-testid="admin-search"
              className="w-full border-b border-gray-400/50 bg-transparent py-2 focus:outline-none focus:border-teal"
            />
          </div>
        </div>

        {loading && (
          <p
            data-testid="admin-leads-loading"
            className="font-mono text-[11px] tracking-[0.24em] uppercase text-gray-600"
          >
            Loading leads…
          </p>
        )}
        {error && (
          <p data-testid="admin-leads-error" className="text-red-600">
            {error}
          </p>
        )}
        {!loading && !error && filtered.length === 0 && (
          <div
            data-testid="admin-leads-empty"
            className="border border-gray-200/70 p-16 text-center"
          >
            <p className="font-display italic text-3xl text-ink">
              No leads yet.
            </p>
            <p className="mt-3 font-mono text-[11px] tracking-[0.24em] uppercase text-gray-600">
              Submissions will appear here as soon as they come in.
            </p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="border border-gray-200/70 overflow-x-auto bg-paper">
            <table
              data-testid="admin-leads-table"
              className="w-full text-sm border-collapse"
            >
              <thead>
                <tr className="bg-teal-light/40">
                  {["Date", "Name", "Phone", "Email", "Campaign", "", ""].map((h, ix) => (
                    <th
                      key={`${h}-${ix}`}
                      className="text-left font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600 px-5 py-4 border-b border-gray-200/70"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((l, i) => {
                  const src = (l.utm_source || "").trim();
                  const med = (l.utm_medium || "").trim();
                  const camp = (l.utm_campaign || "").trim();
                  const hasUtm = src || med || camp;
                  return (
                  <tr
                    key={l.id || i}
                    data-testid={`admin-lead-row-${i}`}
                    className="border-b border-gray-200/60 hover:bg-teal-light/20 transition-colors cursor-pointer"
                    onClick={() => setSelected(l)}
                  >
                    <td className="px-5 py-4 text-gray-800 whitespace-nowrap font-mono text-xs">
                      {fmtDate(l.created_at)}
                    </td>
                    <td className="px-5 py-4 font-medium text-ink">{l.name}</td>
                    <td className="px-5 py-4 text-gray-800">
                      <a
                        href={`tel:${l.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="hover:text-teal"
                      >
                        {l.phone}
                      </a>
                    </td>
                    <td className="px-5 py-4 text-gray-800">
                      <a
                        href={`mailto:${l.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="hover:text-teal"
                      >
                        {l.email}
                      </a>
                    </td>
                    <td className="px-5 py-4 align-top">
                      {hasUtm ? (
                        <div className="flex flex-col gap-0.5 text-[11px] font-mono leading-tight">
                          {src && <span><span className="text-gray-600 mr-1">src:</span><span className="text-ink">{src}</span></span>}
                          {med && <span><span className="text-gray-600 mr-1">med:</span><span className="text-ink">{med}</span></span>}
                          {camp && <span><span className="text-gray-600 mr-1">camp:</span><span className="text-ink">{camp}</span></span>}
                        </div>
                      ) : (
                        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-gray-400">
                          direct · {l.source}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right text-teal font-mono text-[10px] tracking-[0.2em] uppercase">
                      View →
                    </td>
                    <td className="px-3 py-4 text-right">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteLead(l);
                        }}
                        data-testid={`admin-lead-delete-${i}`}
                        title="Delete this lead"
                        className="font-mono text-[10px] tracking-[0.2em] uppercase text-gray-400 hover:text-red-600 transition-colors px-2 py-1"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div
          data-testid="admin-lead-drawer"
          className="fixed inset-0 z-40 bg-ink/40 flex justify-end"
          onClick={() => setSelected(null)}
        >
          <aside
            className="bg-paper w-full max-w-[520px] h-full overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 md:p-10">
              <div className="flex items-center justify-between mb-8">
                <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-gray-600">
                  Lead Detail
                </p>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  data-testid="admin-drawer-close"
                  className="font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600 hover:text-ink"
                >
                  Close ✕
                </button>
              </div>
              <h2 className="font-display text-4xl leading-tight tracking-tight text-ink">
                {selected.name}
              </h2>
              <p className="mt-2 font-mono text-[11px] tracking-[0.2em] uppercase text-gray-600">
                Submitted {fmtDate(selected.created_at)} · {selected.source}
              </p>

              <dl className="mt-10 space-y-6">
                <div>
                  <dt className="font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600">
                    Phone
                  </dt>
                  <dd className="mt-1">
                    <a
                      href={`tel:${selected.phone}`}
                      className="font-display text-2xl text-ink hover:text-teal"
                    >
                      {selected.phone}
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600">
                    Email
                  </dt>
                  <dd className="mt-1">
                    <a
                      href={`mailto:${selected.email}`}
                      className="font-display text-2xl text-ink hover:text-teal break-all"
                    >
                      {selected.email}
                    </a>
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600 mb-2">
                    Message
                  </dt>
                  <dd className="text-[15px] leading-[1.75] text-gray-800 whitespace-pre-wrap border-l-2 border-teal pl-4">
                    {selected.message || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600 mb-2">
                    Campaign Attribution
                  </dt>
                  <dd className="text-[14px] leading-[1.7] text-gray-800">
                    {(() => {
                      const fields = [
                        ["utm_source", "Source"],
                        ["utm_medium", "Medium"],
                        ["utm_campaign", "Campaign"],
                        ["utm_term", "Term"],
                        ["utm_content", "Content"],
                        ["referrer", "Referrer"],
                        ["first_landing", "First Landing"],
                        ["source", "Page"],
                      ];
                      const rows = fields
                        .map(([k, label]) => [label, (selected[k] || "").toString().trim()])
                        .filter(([, v]) => v);
                      if (rows.length === 0) {
                        return (
                          <span className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500">
                            Direct visit · no UTMs captured
                          </span>
                        );
                      }
                      return (
                        <table className="w-full font-mono text-[12px]">
                          <tbody>
                            {rows.map(([label, value]) => (
                              <tr key={label} className="border-b border-gray-200/50 last:border-b-0">
                                <td className="py-1.5 pr-4 text-gray-600 align-top w-[40%] uppercase tracking-[0.18em] text-[10px]">{label}</td>
                                <td className="py-1.5 text-ink break-all">{value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()}
                  </dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      )}
    </main>
  );
}
